/**
 * Centralized Analytics System
 * Handles all tracking: webhooks, redirects, Amplitude, and Slack
 */
import * as amplitude from '@amplitude/analytics-browser';

import { JWTContextValue } from '../contexts/JWTProvider';
import { defaultTheme } from '../themes';
import { SlackTracker, createSlackContext } from './slackNotifications';
import { appendQueryParamsToUrl } from './urlUtils';

export interface AnalyticsContext {
  serviceName?: string;
  serviceId?: number | null;
  fleetCode?: string | null;
  token?: string | null;
  redirectURISuccess?: string | null;
  redirectURIError?: string | null;
  tsp?: string;
  navigate?: (path: string) => void;
  skipReasonParam?: string;
  skipTspParam?: string;
}

export enum UserJourneyEvent {
  // Journey start
  PROCESS_STARTED = 'process_started',

  // Terms acceptance/rejection
  TERMS_ACCEPTED = 'terms_accepted',
  TERMS_REJECTED = 'terms_rejected',

  // TSP selection
  TSP_SELECTED = 'tsp_selected',
  TSP_NOT_FOUND = 'tsp_not_found',
  TSP_SELECTION_QUIT = 'tsp_selection_quit',

  // Credential entry and connection
  CREDENTIALS_ENTERED = 'credentials_entered',
  CONNECTION_SUCCESSFUL = 'connection_successful',

  // Help requests
  HELP_REQUESTED_CREDENTIALS = 'help_requested_credentials',
  HELP_REQUESTED_ERROR = 'help_requested_error',

  // Errors and recovery
  CONNECTION_ERROR = 'connection_error',
  ERROR_QUIT = 'error_quit',

  // Navigation
  BACK_TO_TSP_SELECTION = 'back_to_tsp_selection',

  // TSP management
  TSP_REMOVED = 'tsp_removed',

  // Page views (for tracking navigation)
  PAGE_VIEW = 'page_view',

  // Webhook events (map to documented status codes)
  TSP_ONBOARDING_STARTED = 'tsp_onboarding_started', // When credentials validated
  TSP_ONBOARDING_COMPLETED = 'tsp_onboarding_completed', // When data retrieved
  TSP_ONBOARDING_FAILED = 'tsp_onboarding_failed', // When onboarding fails
}

/**
 * Create analytics context from JWT state and additional data
 */
export function createAnalyticsContext(
  jwt: JWTContextValue,
  serviceName?: string,
  additionalData?: Partial<AnalyticsContext>
): AnalyticsContext {
  // Debug: Check if JWT is properly initialized
  // console.log('[Analytics] JWT state received:', {
  //   exists: jwt.exists,
  //   isValid: jwt.isValid,
  //   isExpired: jwt.isExpired,
  //   rawToken: jwt.rawToken ? `${jwt.rawToken.substring(0, 20)}...` : 'NULL/UNDEFINED',
  //   serviceId: jwt.serviceId,
  //   fleetCode: jwt.fleetCode,
  //   redirectURISuccess: jwt.redirectURISuccess,
  //   redirectURIError: jwt.redirectURIError,
  //   decodedPayload: jwt.decodedPayload,
  //   fullJWT: jwt,
  // });

  // Fallback: Try to get token from URL if JWT state doesn't have it
  let fallbackToken = jwt.rawToken;
  // console.log('[Analytics] Token fallback check:', {
  //   jwtRawToken: jwt.rawToken ? `${jwt.rawToken.substring(0, 20)}...` : 'NULL/UNDEFINED',
  //   windowLocationSearch: window.location.search,
  // });

  if (!fallbackToken) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      // console.log('[Analytics] URL token check:', {
      //   urlToken: urlToken ? `${urlToken.substring(0, 20)}...` : 'NULL/UNDEFINED',
      //   allParams: Object.fromEntries(urlParams),
      // });

      if (urlToken) {
        // console.log('[Analytics] Using fallback token from URL');
        fallbackToken = urlToken;
      } else {
        // Try localStorage as final fallback
        try {
          const localToken = localStorage.getItem('catena_link_token');
          if (localToken) {
            fallbackToken = localToken;
          } else {
          }
        } catch (localError) {
          console.warn(
            '[Analytics] Failed to get token from localStorage:',
            localError
          );
        }
      }
    } catch (error) {
      console.warn('[Analytics] Failed to get fallback token from URL:', error);
    }
  } else {
    console.log('[Analytics] Using JWT rawToken (no fallback needed)');
  }

  // Simplified redirect URI resolution - JWT is now the primary source
  // Since JWT now always includes fallback URIs, we prefer JWT values
  const finalRedirectURISuccess =
    jwt.redirectURISuccess || additionalData?.redirectURISuccess || undefined;
  const finalRedirectURIError =
    jwt.redirectURIError || additionalData?.redirectURIError || undefined;

  console.log('[Analytics] Simplified redirect URI resolution:', {
    jwt: {
      redirectURISuccess: jwt.redirectURISuccess,
      redirectURIError: jwt.redirectURIError,
    },
    additionalContext: {
      redirectURISuccess: additionalData?.redirectURISuccess,
      redirectURIError: additionalData?.redirectURIError,
    },
    final: {
      redirectURISuccess: finalRedirectURISuccess,
      redirectURIError: finalRedirectURIError,
    },
    note: 'JWT now includes fallback URIs, so JWT values take precedence',
  });

  const context = {
    serviceName: jwt.serviceName || serviceName, // Use JWT service name first, then fallback
    serviceId: jwt.serviceId || undefined,
    fleetCode: jwt.fleetCode || undefined,
    // JWT redirect URIs now include fallbacks, so they take precedence
    redirectURISuccess: finalRedirectURISuccess,
    redirectURIError: finalRedirectURIError,
    token: fallbackToken || undefined,
    ...additionalData,
  };

  if (additionalData?.skipReasonParam !== undefined) {
    const trimmedReason = additionalData.skipReasonParam
      ? additionalData.skipReasonParam.trim()
      : '';
    context.skipReasonParam = trimmedReason || undefined;
  }

  if (additionalData?.skipTspParam !== undefined) {
    const trimmedTsp = additionalData.skipTspParam
      ? additionalData.skipTspParam.trim()
      : '';
    context.skipTspParam = trimmedTsp || undefined;
  }

  // console.log('[Analytics] Created analytics context:', {
  //   jwt: {
  //     serviceId: jwt.serviceId,
  //     fleetCode: jwt.fleetCode,
  //     isValid: jwt.isValid,
  //     hasToken: !!jwt.rawToken,
  //     rawToken: jwt.rawToken ? `${jwt.rawToken.substring(0, 20)}...` : 'undefined',
  //     exists: jwt.exists,
  //     isExpired: jwt.isExpired,
  //   },
  //   context: {
  //     ...context,
  //     token: context.token ? `${context.token.substring(0, 20)}...` : 'undefined',
  //   },
  // });

  return context;
}

/**
 * Central analytics tracking function
 */
export async function trackUserJourney(
  event: UserJourneyEvent,
  context: AnalyticsContext,
  details?: Record<string, any>
): Promise<void> {
  try {
    // Track in Amplitude
    await trackAmplitude(event, context, details);

    // Track in Slack
    await trackSlack(event, context, details);

    // Handle webhooks/redirects for terminal events
    await handleTerminalEvents(event, context, details);
  } catch (error) {
    // Graceful failure - don't break user experience
    console.warn('[Analytics] Error tracking event:', error);
  }
}

/**
 * Track event in Amplitude
 */
async function trackAmplitude(
  event: UserJourneyEvent,
  context: AnalyticsContext,
  details?: Record<string, any>
): Promise<void> {
  const amplitudeEventName = getAmplitudeEventName(event);
  const amplitudeProperties = {
    serviceName: context.serviceName,
    serviceId: context.serviceId,
    fleetCode: context.fleetCode,
    tsp: context.tsp,
    ...details,
  };

  amplitude.track(amplitudeEventName, amplitudeProperties);
}

/**
 * Track event in Slack
 */
async function trackSlack(
  event: UserJourneyEvent,
  context: AnalyticsContext,
  details?: Record<string, any>
): Promise<void> {
  // Ensure token is available before sending Slack notifications
  if (!context.token) {
    console.warn(
      '[Analytics] No token available for Slack notification, skipping'
    );
    return;
  }

  // Store token in localStorage to ensure axios interceptor can access it
  const { tokenStorage } = await import('./tokenStorage');
  tokenStorage.set(context.token);

  // Create JWT-like object for Slack context
  const jwtLike: JWTContextValue = {
    exists: !!context.token,
    isValid: true,
    isExpired: false,
    isDecodable: true,
    rawToken: context.token || null,
    decodedPayload: null,
    serviceId: context.serviceId || null,
    serviceName: context.serviceName || null,
    fleetCode: context.fleetCode || null,
    redirectURISuccess: context.redirectURISuccess || null,
    redirectURIError: context.redirectURIError || null,
    theme: defaultTheme,
    errorType: null,
    errorMessage: null,
    canRedirectToService: false,
    preferredRedirectURI: null,
    setToken: () => {},
    clearToken: () => {},
    getAuthHeader: () => '',
    setupAnalytics: () => {},
    setServiceName: () => {},
  };

  const slackContext = createSlackContext(jwtLike, context.serviceName, {
    tsp: context.tsp,
  });

  switch (event) {
    case UserJourneyEvent.PROCESS_STARTED:
      await SlackTracker.onboardingStarted(slackContext, jwtLike);
      break;

    case UserJourneyEvent.TERMS_ACCEPTED:
      // Terms accepted - no page view needed here as it should be tracked when page loads
      break;

    case UserJourneyEvent.TERMS_REJECTED:
      SlackTracker.onboardingSkipped(slackContext, 'User rejected terms');
      break;

    case UserJourneyEvent.TSP_SELECTED:
      SlackTracker.tspSelected(
        slackContext,
        context.tsp || 'unknown',
        details?.tspName || context.tsp || 'unknown'
      );
      break;

    case UserJourneyEvent.TSP_NOT_FOUND:
      SlackTracker.onboardingSkipped(
        slackContext,
        'TSP not found - returned to partner'
      );
      break;

    case UserJourneyEvent.TSP_SELECTION_QUIT:
      SlackTracker.onboardingSkipped(
        slackContext,
        'User quit from TSP selection'
      );
      break;

    case UserJourneyEvent.CREDENTIALS_ENTERED:
      SlackTracker.credentialsEntered(
        slackContext,
        context.tsp || 'unknown',
        details?.credentialType || 'unknown',
        details?.isUpdate || false
      );
      break;

    case UserJourneyEvent.CONNECTION_SUCCESSFUL:
      SlackTracker.onboardingCompleted(slackContext, context.tsp || 'unknown');
      break;

    case UserJourneyEvent.HELP_REQUESTED_CREDENTIALS:
      SlackTracker.helpRequested(
        slackContext,
        'credentials_help',
        details?.page || 'unknown'
      );
      break;

    case UserJourneyEvent.HELP_REQUESTED_ERROR:
      SlackTracker.helpRequested(
        slackContext,
        'error_help',
        details?.page || 'unknown'
      );
      break;

    case UserJourneyEvent.CONNECTION_ERROR:
      SlackTracker.errorEncountered(
        slackContext,
        details?.errorType || 'unknown',
        details?.errorMessage || 'unknown',
        details?.page || 'unknown'
      );
      break;

    case UserJourneyEvent.ERROR_QUIT:
      SlackTracker.onboardingSkipped(
        slackContext,
        `User quit from error: ${details?.errorType || 'unknown'}`
      );
      break;

    case UserJourneyEvent.BACK_TO_TSP_SELECTION:
      SlackTracker.backNavigation(slackContext, details?.fromTsp);
      break;

    case UserJourneyEvent.PAGE_VIEW:
      SlackTracker.pageView(slackContext, details?.page || 'unknown');
      break;
  }
}

/**
 * Handle terminal events that require webhooks or redirects
 * Uses the existing /v1/service/onboard/abandoned endpoint for user quit scenarios
 */
async function handleTerminalEvents(
  event: UserJourneyEvent,
  context: AnalyticsContext,
  details?: Record<string, any>
): Promise<void> {
  // console.log('[Analytics] handleTerminalEvents called:', {
  //   event,
  //   hasToken: !!context.token,
  //   context,
  //   details,
  // });

  if (!context.token) {
    console.warn(
      '[Analytics] No token available for terminal event, skipping webhook'
    );
    return;
  }

  // Handle different terminal events
  switch (event) {
    case UserJourneyEvent.CONNECTION_SUCCESSFUL:
      // For successful connections, redirect to success page
      // The backend /tsp/onboard endpoint already sends the success webhook
      // No need to redirect here since Onboard/TSPConfig pages now navigate to /success directly
      console.log(
        '[Analytics] Connection successful - navigation handled by onboard pages'
      );
      break;

    case UserJourneyEvent.TERMS_REJECTED:
    case UserJourneyEvent.TSP_NOT_FOUND:
    case UserJourneyEvent.TSP_SELECTION_QUIT:
    case UserJourneyEvent.ERROR_QUIT:
      // For user quit scenarios, use the existing abandoned endpoint
      console.log('[Analytics] Calling abandoned endpoint for event:', event);
      await callAbandonedEndpoint(context, event, details);
      break;

    case UserJourneyEvent.CREDENTIALS_ENTERED:
    case UserJourneyEvent.CONNECTION_ERROR:
      // These are handled by the backend /tsp/onboard endpoint automatically
      // No additional webhook calls needed from frontend
      console.log('[Analytics] Event handled by backend:', event);
      break;
  }
}

/**
 * Call the existing /v1/service/onboard/abandoned endpoint for user quit scenarios
 * This endpoint automatically sends the proper webhook to the customer
 */
async function callAbandonedEndpoint(
  context: AnalyticsContext,
  event: UserJourneyEvent,
  details?: Record<string, any>
): Promise<void> {
  try {
    console.log('[Analytics] Calling abandoned endpoint for:', event);

    // Ensure token is available for axios interceptor
    if (!context.token) {
      console.warn(
        '[Analytics] No token available - cannot call abandoned endpoint'
      );
      return;
    }

    const { tokenStorage } = await import('./tokenStorage');
    tokenStorage.set(context.token);

    const axios = (await import('../axios')).default;

    // Create payload for abandoned endpoint
    const payload = {
      page_on_unload: getPageOnUnload(event, details),
      selected_tsp: context.tsp || 'unknown',
    };

    console.log('[Analytics] Abandoned endpoint payload:', payload);

    const response = await axios.post('/service/onboard/abandoned', payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      console.log('[Analytics] Successfully called abandoned endpoint');
    } else {
      console.warn(
        '[Analytics] Unexpected abandoned endpoint response:',
        response.status
      );
    }
  } catch (error: any) {
    // Graceful failure - don't break user flow
    console.error('[Analytics] Error calling abandoned endpoint:', {
      error: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
  }
}

/**
 * Get appropriate page_on_unload value for abandoned endpoint based on event
 */
function getPageOnUnload(
  event: UserJourneyEvent,
  details?: Record<string, any>
): string {
  switch (event) {
    case UserJourneyEvent.TERMS_REJECTED:
      return 'approve_page';
    case UserJourneyEvent.TSP_SELECTION_QUIT:
      return 'select_tsp_page';
    case UserJourneyEvent.TSP_NOT_FOUND:
      return 'tsp_not_found_button';
    case UserJourneyEvent.ERROR_QUIT:
      // Use the page from details if available, otherwise default
      return details?.page === 'Onboard' ? 'onboard_page' : 'error_page';
    default:
      return 'escape_hatch_button';
  }
}

/**
 * Map user journey events to Amplitude event names
 */
function getAmplitudeEventName(event: UserJourneyEvent): string {
  switch (event) {
    case UserJourneyEvent.PROCESS_STARTED:
      return 'Onboarding Process Started';
    case UserJourneyEvent.TERMS_ACCEPTED:
      return 'User Began Onboarding';
    case UserJourneyEvent.TERMS_REJECTED:
      return 'User Rejected Terms';
    case UserJourneyEvent.TSP_SELECTED:
      return 'TSP Selected';
    case UserJourneyEvent.TSP_NOT_FOUND:
      return 'TSP Not Found - Returned to Partner';
    case UserJourneyEvent.TSP_SELECTION_QUIT:
      return 'User Quit from TSP Selection';
    case UserJourneyEvent.CREDENTIALS_ENTERED:
      return 'Credentials Entered';
    case UserJourneyEvent.CONNECTION_SUCCESSFUL:
      return 'Onboarding Completed Successfully';
    case UserJourneyEvent.HELP_REQUESTED_CREDENTIALS:
      return 'Help Requested - Credentials';
    case UserJourneyEvent.HELP_REQUESTED_ERROR:
      return 'Help Requested - Error';
    case UserJourneyEvent.CONNECTION_ERROR:
      return 'Connection Error Encountered';
    case UserJourneyEvent.ERROR_QUIT:
      return 'User Quit from Error';
    case UserJourneyEvent.BACK_TO_TSP_SELECTION:
      return 'User Navigated Back to TSP Selection';
    case UserJourneyEvent.PAGE_VIEW:
      return 'Page Viewed';
    case UserJourneyEvent.TSP_ONBOARDING_STARTED:
      return 'TSP Onboarding Started (Webhook)';
    case UserJourneyEvent.TSP_ONBOARDING_COMPLETED:
      return 'TSP Onboarding Completed (Webhook)';
    case UserJourneyEvent.TSP_ONBOARDING_FAILED:
      return 'TSP Onboarding Failed (Webhook)';
    default:
      return 'Unknown Event';
  }
}

/**
 * Convenience functions for common tracking scenarios
 */
export const Analytics = {
  /**
   * Track when user starts the process (valid JWT, page loaded)
   */
  processStarted: (context: AnalyticsContext) =>
    trackUserJourney(UserJourneyEvent.PROCESS_STARTED, context),

  /**
   * Track when user accepts terms and continues
   */
  termsAccepted: (context: AnalyticsContext, nextPage: string) =>
    trackUserJourney(UserJourneyEvent.TERMS_ACCEPTED, context, { nextPage }),

  /**
   * Track when user rejects terms and skips (just tracking, no redirect)
   */
  termsRejected: (context: AnalyticsContext) =>
    trackUserJourney(UserJourneyEvent.TERMS_REJECTED, context),

  /**
   * Track TSP selection
   */
  tspSelected: (context: AnalyticsContext, tsp: string, tspName: string) =>
    trackUserJourney(
      UserJourneyEvent.TSP_SELECTED,
      { ...context, tsp },
      { tspName }
    ),

  /**
   * Track when user can't find their TSP
   */
  tspNotFound: (context: AnalyticsContext) =>
    trackUserJourney(UserJourneyEvent.TSP_NOT_FOUND, context),

  /**
   * Track when user quits from TSP selection (just tracking, no redirect)
   */
  tspSelectionQuit: (context: AnalyticsContext) =>
    trackUserJourney(UserJourneyEvent.TSP_SELECTION_QUIT, context),

  /**
   * Track credential entry
   */
  credentialsEntered: (
    context: AnalyticsContext,
    tsp: string,
    credentialType: string,
    isUpdate = false
  ) =>
    trackUserJourney(
      UserJourneyEvent.CREDENTIALS_ENTERED,
      { ...context, tsp },
      { credentialType, isUpdate }
    ),

  /**
   * Track successful connection
   */
  connectionSuccessful: (context: AnalyticsContext, tsp: string) =>
    trackUserJourney(UserJourneyEvent.CONNECTION_SUCCESSFUL, {
      ...context,
      tsp,
    }),

  /**
   * Track help requests
   */
  helpRequestedCredentials: (context: AnalyticsContext, page: string) =>
    trackUserJourney(UserJourneyEvent.HELP_REQUESTED_CREDENTIALS, context, {
      page,
    }),

  helpRequestedError: (context: AnalyticsContext, page: string) =>
    trackUserJourney(UserJourneyEvent.HELP_REQUESTED_ERROR, context, { page }),

  /**
   * Track connection errors
   */
  connectionError: (
    context: AnalyticsContext,
    errorType: string,
    errorMessage: string,
    page: string,
    errorDetails?: any
  ) =>
    trackUserJourney(UserJourneyEvent.CONNECTION_ERROR, context, {
      errorType,
      errorMessage,
      page,
      ...(errorDetails && {
        caption: errorDetails.caption,
        showInline: errorDetails.showInline,
        retryable: errorDetails.retryable,
        helpText: errorDetails.helpText,
        showEscapeHatch: errorDetails.showEscapeHatch,
      }),
    }),

  /**
   * Track when user quits from error
   */
  errorQuit: (context: AnalyticsContext, errorType: string, page: string) =>
    trackUserJourney(UserJourneyEvent.ERROR_QUIT, context, { errorType, page }),

  /**
   * Track back navigation to TSP selection
   */
  backToTspSelection: (context: AnalyticsContext, fromTsp: string) =>
    trackUserJourney(UserJourneyEvent.BACK_TO_TSP_SELECTION, context, {
      fromTsp,
    }),

  /**
   * Track TSP removal
   */
  tspRemoved: (context: AnalyticsContext, tsp: string, tspName: string) =>
    trackUserJourney(
      UserJourneyEvent.TSP_REMOVED,
      { ...context, tsp },
      { tspName }
    ),

  /**
   * Track page views
   */
  pageView: (context: AnalyticsContext, page: string) =>
    trackUserJourney(UserJourneyEvent.PAGE_VIEW, context, { page }),

  /**
   * Handle confirmed skip actions (always redirect to skip completed page)
   */
  confirmedSkip: {
    /**
     * Handle confirmed terms rejection (redirect to skip completed page)
     */
    termsRejected: async (context: AnalyticsContext) => {
      // Track the event and send abandoned webhook
      await trackUserJourney(UserJourneyEvent.TERMS_REJECTED, context);

      const redirectPath = buildSkipCompletedPath(context);

      // Use the navigation callback if provided, otherwise fallback to window.location
      if (context.navigate) {
        context.navigate(redirectPath);
      } else {
        console.warn(
          '[Analytics] No navigation callback provided, using window.location'
        );
        window.location.href = redirectPath;
      }
    },

    /**
     * Handle confirmed TSP selection quit (redirect to skip completed page)
     */
    tspSelectionQuit: async (context: AnalyticsContext) => {
      // Track the event and send abandoned webhook
      await trackUserJourney(UserJourneyEvent.TSP_SELECTION_QUIT, context);

      const redirectPath = buildSkipCompletedPath(context);

      // Use the navigation callback if provided, otherwise fallback to window.location
      if (context.navigate) {
        context.navigate(redirectPath);
      } else {
        console.warn(
          '[Analytics] No navigation callback provided, using window.location'
        );
        window.location.href = redirectPath;
      }
    },

    /**
     * Handle confirmed TSP not found (redirect to skip completed page)
     */
    tspNotFound: async (context: AnalyticsContext) => {
      // Track the event and send abandoned webhook
      await trackUserJourney(UserJourneyEvent.TSP_NOT_FOUND, context);

      const redirectPath = buildSkipCompletedPath(context);

      // Use the navigation callback if provided, otherwise fallback to window.location
      if (context.navigate) {
        context.navigate(redirectPath);
      } else {
        console.warn(
          '[Analytics] No navigation callback provided, using window.location'
        );
        window.location.href = redirectPath;
      }
    },

    /**
     * Handle confirmed error quit (redirect to skip completed page)
     */
    errorQuit: async (
      context: AnalyticsContext,
      errorType: string,
      page: string
    ) => {
      // Track the event and send abandoned webhook
      await trackUserJourney(UserJourneyEvent.ERROR_QUIT, context, {
        errorType,
        page,
      });

      const redirectPath = buildSkipCompletedPath(context);

      // Use the navigation callback if provided, otherwise fallback to window.location
      if (context.navigate) {
        context.navigate(redirectPath);
      } else {
        console.warn(
          '[Analytics] No navigation callback provided, using window.location'
        );
        window.location.href = redirectPath;
      }
    },
  },
};

function buildSkipCompletedPath(context: AnalyticsContext): string {
  return appendQueryParamsToUrl('/skip-completed', {
    skip_reason: context.skipReasonParam,
    skip_tsp_name: context.skipTspParam,
  });
}
